import type { PaperProps } from '@mui/material'
import {
  Box,
  Paper,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
} from '@mui/material'
import * as React from 'react'

interface NetworkToggleProps extends PaperProps {
  network: 'Mainnet' | 'Testnet'
  onNetworkChange: (value: 'Mainnet' | 'Testnet') => void
}

const ButtonWidth = 100

const StyledWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}))

const AnimatedBackground = styled(Box, { shouldForwardProp: prop => prop !== 'activeIndex' && prop !== 'network' })<{
  activeIndex: number
  network: 'Mainnet' | 'Testnet'
}>(({ activeIndex, network }) => ({
  position: 'absolute',
  top: 5,
  left: 5,
  height: 'calc(100% - 10px)',
  width: ButtonWidth - 10,
  background: network === 'Mainnet' ? '#6630F1' : '#2790C6',
  borderRadius: 6,
  transform: `translateX(${activeIndex * ButtonWidth}px)`,
  transition: 'transform 300ms ease, background-color 300ms ease',
  zIndex: 1,
}))

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    'width': ButtonWidth,
    'border': 0,
    'borderRadius': 0,
    'color': theme.palette.text.primary,
    '&.Mui-selected': {
      'backgroundColor': 'transparent',
      'color': theme.palette.common.white,
      'fontWeight': 600,
      '&:hover': { backgroundColor: 'transparent' },
    },
    '&:hover': { backgroundColor: 'transparent' },
  },
}))

export const NetworkToggle: React.FC<NetworkToggleProps> = ({
  network,
  onNetworkChange,
  ...rest
}) => {
  const handleChange = (
    _: React.MouseEvent<HTMLElement>,
    newNetwork: 'Mainnet' | 'Testnet' | null,
  ) => {
    if (newNetwork !== null) {
      onNetworkChange(newNetwork)
    }
  }

  const activeIndex = network === 'Mainnet' ? 0 : 1

  return (
    <Paper
      elevation={0}
      sx={theme => ({
        border: `1px solid ${theme.palette.divider}`,
        display: 'inline-block',
      })}
      {...rest}
    >
      <StyledWrapper>
        <AnimatedBackground activeIndex={activeIndex} network={network} />
        <StyledToggleButtonGroup
          exclusive
          value={network}
          onChange={handleChange}
          aria-label="network toggle"
        >
          <ToggleButton disableRipple value="Mainnet">Mainnet</ToggleButton>
          <ToggleButton disableRipple value="Testnet">Testnet*</ToggleButton>
        </StyledToggleButtonGroup>
      </StyledWrapper>
    </Paper>
  )
}
